<template>
  <div class="app-container">
    <Container
      :title="t('route.acceptanceBillsList')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :hasCreate="false /*$hasPermission(['approvalform_M'])*/"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <el-row>
          <el-col
            :span="12"
          >
            <el-button
              type="primary"
              size="large"
              style="margin-bottom: 10px"
              @click="handleExport"
            >
              {{ t('common.exportExcel') }}
            </el-button>
          </el-col>
        </el-row>
        <SimpleTable
          ref="tableRef"
          :listLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :operateColumnWidth="/*$hasPermission(['approvalform_M'])*/ true ? 240:80"
        >
          <template
            #default="slotProps"
          >
            <el-button
              @click="viewDetail(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('Acceptance.viewDetails') }}
            </el-button>
          </template>
        </SimpleTable>
        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100, 200]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import SearchForm from '../../../components/form/search/SearchForm.vue'
import SimpleTable from '../../../components/table/simple/SimpleTable.vue'
import Container from '../../../components/form/container/Container.vue'
import { useI18n } from 'vue-i18n'
import { formatDate, formatThousands } from '@/utils'
import { createModel } from '@/utils/component'
import { ElMessage } from 'element-plus'
import { formatBanendFlag, formatDraftType, formatSplitFlag } from '@/utils/acceptanceBillFormatUtil'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'
import { exportAvailableAcceptance, getAvailableAcceptancePageList } from '@/apis/accepted-bill/availableAcceptance'
import { AvailableAcceptanceModel } from '@/model/paymentSlip/paymentSlipModel'
import { AvailableAcceptancePageParamModel } from '@/model/accepted-bill/availableAcceptanceModel'
import router from '@/router'
import dayjs from 'dayjs'

export default defineComponent({
  name: 'AcceptanceBillsPageList',
  components: {
    SearchForm,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const tableRef = ref()
    const paramModelRef = ref<AvailableAcceptancePageParamModel | null>(null)
    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      { prop: 'bankName', label: t('Acceptance.bankName'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'dueDate',
        label: t('order.cutOffDate'),
        component: 'el-date-picker',
        type: 'date',
        model: '',
        suffix: '前'
      },
      { prop: 'draftNo', label: t('Acceptance.draftNo'), component: 'el-input', type: 'text', model: '' }
    ]
    const tableStruct = [
      { prop: 'id', label: t('Acceptance.id'), width: 80 },
      { prop: 'companyCode', label: t('company.companyCode'), width: 100 },
      { prop: 'draftNo', label: t('Acceptance.draftNo') },
      {
        prop: 'draftType',
        label: t('pay.draftType'),
        formatter: (row: any) => formatDraftType(row.draftType),
        width: 100
      },
      {
        prop: 'splitFlag',
        label: t('pay.splitFlag'),
        formatter: (row: any) => formatSplitFlag(row.splitFlag),
        width: 100
      },
      {
        prop: 'issueDate',
        label: t('Acceptance.issueDate'),
        formatter: (row: AvailableAcceptanceModel) => formatDate(row.issueDate, 'yyyy-MM-dd')
      },
      {
        prop: 'dueDate',
        label: t('Acceptance.dueDate'),
        formatter: (row: AvailableAcceptanceModel) => formatDate(row.dueDate, 'yyyy-MM-dd')
      },
      { prop: 'bankName', label: t('Acceptance.bankName') },
      { prop: 'banendFlag', label: t('pay.banendFlag'), formatter: (row: any) => formatBanendFlag(row.banendFlag) },
      {
        prop: 'draftAmt',
        label: t('pay.draftAmt'),
        formatter: (row: any) => formatThousands(row.draftAmt),
        align: 'right',
        width: 200
      }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as any,
      tableData: Array<AvailableAcceptanceModel>(),
      columnStruct: tableStruct,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true
        let dueDateStr = null
        if (dataMap.searchFormModel.dueDate) {
          dueDateStr = dayjs(dataMap.searchFormModel.dueDate).format('YYYY-MM-DD HH:mm:ss')
        }
        const params: AvailableAcceptancePageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          bankName: dataMap.searchFormModel.bankName,
          draftNo: dataMap.searchFormModel.draftNo,
          dueDate: dueDateStr,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getAvailableAcceptancePageList(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
          if (data?.code !== 200 && data?.msg) {
            ElMessage({
              message: data.msg,
              type: 'error',
              duration: 2000
            })
          }
        }
        dataMap.total = data?.data?.totalCount ?? 0
        paramModelRef.value = params
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      },
      async handleExport() {
        if (!paramModelRef.value) {
          alert('请先进行检索')
          return
        }
        const params: AvailableAcceptancePageParamModel = {
          companyCode: paramModelRef.value.companyCode,
          bankName: paramModelRef.value.bankName,
          draftNo: paramModelRef.value.draftNo,
          dueDate: paramModelRef.value.dueDate,
          pageCurrent: paramModelRef.value.pageCurrent,
          pageSize: paramModelRef.value.pageSize
        }
        const urlBlob = await exportAvailableAcceptance(params)
        if (urlBlob) {
          const link = document.createElement('a')
          link.href = urlBlob
          link.download = '承兑票清单.xlsx'
          link.click()
          window.URL.revokeObjectURL(urlBlob)
        } else {
          ElMessage({
            message: '获取excel失败',
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    const viewDetail = (row: AvailableAcceptanceModel) => {
      router.push({ path: `availableAcceptanceDetail/${row.id}` })
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    return {
      t,
      tableRef,
      viewDetail,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style scoped>

</style>
